// RiskAssessmentScoring - Risk Assessment Matrix scoring interface
// Implements US-3.3: Matrix Scoring Frameworks (Value/Effort, Eisenhower, Risk Assessment)

import React, { useState, useEffect, useCallback } from 'react';
import styles from './MatrixScoring.module.css';
import { Item, Score, CalculatedResult } from '../types/domain';
import { updateScores } from '../services/scoreService';

interface RiskAssessmentScoringProps {
  item: Item;
  isReadOnly: boolean;
  onScoresUpdated?: (itemId: string, scores: Score[], result: CalculatedResult) => void;
}

/**
 * Risk Assessment Matrix scoring component
 * - Displays 2 input fields: Probability (1-5), Impact (1-5)
 * - Calculates and displays risk score: Probability × Impact
 * - Shows risk level: High (20-25), Medium (10-16), Low (1-6)
 * - Auto-saves on value change with debouncing
 */
export const RiskAssessmentScoring: React.FC<RiskAssessmentScoringProps> = ({
  item,
  isReadOnly,
  onScoresUpdated,
}) => {
  // EXTRACT current scores from item
  const getScoreValue = (criterion: string): number => {
    const score = item.scores?.find((s) => s.criterion === criterion);
    return score ? Number(score.value) : 0;
  };

  // STATE for score inputs
  const [probability, setProbability] = useState<number>(getScoreValue('probability'));
  const [impact, setImpact] = useState<number>(getScoreValue('impact'));
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // UPDATE local state when item scores change (real-time updates from other users)
  useEffect(() => {
    setProbability(getScoreValue('probability'));
    setImpact(getScoreValue('impact'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.scores]);

  /**
   * CALCULATE risk score and level locally for immediate feedback
   * Score: Probability × Impact
   * 7-level gradient: Critical, High, Medium-High, Medium, Medium-Low, Low, Very Low
   * NOTE: No useCallback needed - function is only called inline, not passed as prop
   */
  const calculateRisk = (p: number, i: number): { score: number; level: string } | null => {
    if (p === 0 || i === 0) return null;

    const score = p * i;
    let level = 'Sehr niedrig';

    if (score >= 21) {
      level = 'Kritisch';
    } else if (score >= 17) {
      level = 'Hoch';
    } else if (score >= 14) {
      level = 'Mittel-Hoch';
    } else if (score >= 11) {
      level = 'Mittel';
    } else if (score >= 8) {
      level = 'Mittel-Niedrig';
    } else if (score >= 5) {
      level = 'Niedrig';
    }

    return { score, level };
  };

  const calculatedRisk = calculateRisk(probability, impact);

  /**
   * SAVE scores to backend
   * NOTE: Debounced to avoid excessive API calls
   */
  const saveScores = useCallback(
    async (p: number, i: number) => {
      // VALIDATE: Only save if both values are set
      if (p === 0 || i === 0) {
        return;
      }

      try {
        setIsSaving(true);
        setError(null);

        const response = await updateScores(item.id, [
          { criterion: 'probability', value: p },
          { criterion: 'impact', value: i },
        ]);

        if (!response.success) {
          throw new Error(response.error || 'Failed to save scores');
        }

        // NOTIFY parent component of score update
        if (onScoresUpdated && response.scores && response.calculatedResult) {
          onScoresUpdated(item.id, response.scores, response.calculatedResult);
        }
      } catch (err) {
        console.error('Failed to save scores:', err);
        setError(err instanceof Error ? err.message : 'Failed to save scores');
      } finally {
        setIsSaving(false);
      }
    },
    [item.id, onScoresUpdated]
  );

  /**
   * DEBOUNCE score saving (wait 1 second after last change)
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isReadOnly && probability > 0 && impact > 0) {
        saveScores(probability, impact);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [probability, impact, isReadOnly, saveScores]);

  /**
   * HANDLE input change with validation
   */
  const handleInputChange = (criterion: 'probability' | 'impact', inputValue: string) => {
    const numValue = parseInt(inputValue, 10);

    // VALIDATE: Empty input
    if (inputValue === '') {
      criterion === 'probability' ? setProbability(0) : setImpact(0);
      return;
    }

    // VALIDATE: Must be number between 1-5
    if (isNaN(numValue) || numValue < 1 || numValue > 5) {
      return;
    }

    // UPDATE state
    criterion === 'probability' ? setProbability(numValue) : setImpact(numValue);
  };

  /**
   * GET risk level color class for visual distinction (7-level gradient)
   */
  const getRiskLevelClass = (level: string): string => {
    switch (level) {
      case 'Kritisch':
        return styles.riskCritical || '';
      case 'Hoch':
        return styles.riskHigh || '';
      case 'Mittel-Hoch':
        return styles.riskMediumHigh || '';
      case 'Mittel':
        return styles.riskMedium || '';
      case 'Mittel-Niedrig':
        return styles.riskMediumLow || '';
      case 'Niedrig':
        return styles.riskLow || '';
      case 'Sehr niedrig':
        return styles.riskVeryLow || '';
      default:
        return '';
    }
  };

  return (
    <div className={styles.matrixScoring}>
      <div className={styles.criteriaGrid}>
        {/* PROBABILITY input */}
        <div className={styles.criteriaField}>
          <label htmlFor={`probability-${item.id}`}>Wahrscheinlichkeit</label>
          <input
            id={`probability-${item.id}`}
            type="number"
            min="1"
            max="5"
            value={probability || ''}
            onChange={(e) => handleInputChange('probability', e.target.value)}
            disabled={isReadOnly}
            placeholder="1-5"
            className={styles.input}
          />
        </div>

        {/* IMPACT input */}
        <div className={styles.criteriaField}>
          <label htmlFor={`impact-${item.id}`}>Impact</label>
          <input
            id={`impact-${item.id}`}
            type="number"
            min="1"
            max="5"
            value={impact || ''}
            onChange={(e) => handleInputChange('impact', e.target.value)}
            disabled={isReadOnly}
            placeholder="1-5"
            className={styles.input}
          />
        </div>
      </div>

      {/* CALCULATED RISK display */}
      {calculatedRisk && (
        <div className={`${styles.resultDisplay} ${getRiskLevelClass(calculatedRisk.level)}`}>
          <span className={styles.resultLabel}>Risiko-Score:</span>
          <span className={styles.resultValue}>{calculatedRisk.score}</span>
          <span className={styles.riskLevel}>({calculatedRisk.level})</span>
          {isSaving && <span className={styles.savingIndicator}>Speichere...</span>}
        </div>
      )}

      {/* ERROR message */}
      {error && <div className={styles.errorMessage}>{error}</div>}

      {/* UNSCORED indicator */}
      {!calculatedRisk && !isReadOnly && (
        <div className={styles.unscoredMessage}>Gib beide Kriterien ein, um den Risiko-Score zu berechnen</div>
      )}
    </div>
  );
};
